"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import HeroParallaxBase from "../HeroParallaxBase";
import AppScreenshot from "@/assets/images/screenshots/app/cracked_100.webp";
import styles from "./HeroFloatToFortune.module.css";
import { DownloadButtonPair } from "@/components/ui/DownloadButton";
import { landingContent } from "@/data/landingContent";
import FeatureBeacon from "../FeatureBeacon";

// Trust badge images (white versions for hero)
import FDICLogo from "@/assets/images/hero/trust/fdic-white.svg";
import SIPCLogo from "@/assets/images/hero/trust/sipc-white.svg";
import AppStoreReviewsLogo from "@/assets/images/hero/trust/appStoreReviews-white.svg";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

const floatAnimation = {
  animate: {
    y: [0, -12, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

const phoneAnimation = {
  hidden: { opacity: 0, y: 60, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      delay: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

export default function HeroFloatToFortune() {
  const { hero } = landingContent;

  return (
    <HeroParallaxBase hideDisclaimer>
      <div className={styles.heroContent}>
        {/* Animated Feature Beacon */}
        <FeatureBeacon />

        {/* Main Headline */}
        <motion.h1
          className={styles.headline}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0.1}
        >
          {hero.headline.line1}
          <span className={styles.headlineAccent}>{hero.headline.line2}</span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          className={styles.subheadline}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0.2}
        >
          {hero.subheadline}
        </motion.p>

        {/* Trust Signals - SVG Badges */}
        <motion.div
          className={styles.trustBadgesRow}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0.3}
        >
          <div className={styles.trustBadgeWrapFdic}>
            <Image
              src={FDICLogo}
              alt="FDIC Insured"
              width={60}
              height={42}
              style={{ width: "auto", height: "42px" }}
            />
          </div>
          <div className={styles.trustBadgeWrapAppStore}>
            <Image
              src={AppStoreReviewsLogo}
              alt="4.8 Star App Store Rating"
              width={157}
              height={110}
              style={{ width: "auto", height: "110px" }}
            />
          </div>
          <div className={styles.trustBadgeWrapSipc}>
            <Image
              src={SIPCLogo}
              alt="SIPC Protected"
              width={77}
              height={54}
              style={{ width: "auto", height: "54px" }}
            />
          </div>
        </motion.div>

        {/* Phone Showcase */}
        <motion.div
          className={styles.phoneShowcase}
          variants={phoneAnimation}
          initial="hidden"
          animate="visible"
        >
          <div className={styles.phoneGlow} />

          <motion.div
            className={styles.phoneWrapper}
            animate={floatAnimation.animate}
          >
            <div className={styles.phoneFrame}>
              <div className={styles.phoneNotch} />
              <div className={styles.phoneScreen}>
                <Image
                  src={AppScreenshot}
                  alt="Cove app showing your personal island with decorations representing your savings"
                  className={styles.phoneImage}
                  priority
                  placeholder="blur"
                />
              </div>
            </div>
          </motion.div>

          {/* Floating Badges */}
          <motion.div
            className={`${styles.floatingElement}`}
            style={{ top: "8%", right: "-42%" }}
            animate={{
              y: [0, -10, 0],
              rotate: [0, 2, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className={styles.floatingBadge}>
              <span className={styles.floatingIcon}>&#127793;</span>
              <span>{hero.floatingBadges.saved}</span>
            </div>
          </motion.div>

          <motion.div
            className={`${styles.floatingElement}`}
            style={{ top: "55%", left: "-30%" }}
            animate={{
              y: [0, -8, 0],
              rotate: [0, -2, 0],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5,
            }}
          >
            <div className={styles.floatingBadge}>
              <span className={styles.floatingIcon}>&#129389;</span>
              <span>{hero.floatingBadges.streak}</span>
            </div>
          </motion.div>

          <motion.div
            className={`${styles.floatingElement}`}
            style={{ bottom: "18%", right: "-25%" }}
            animate={{
              y: [0, -12, 0],
              rotate: [0, 3, 0],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 3,
            }}
          >
            <div className={styles.floatingBadge}>
              <span className={styles.floatingIcon}>&#127942;</span>
              <span>{hero.floatingBadges.rareItem}</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Social Proof */}
        <motion.div
          className={styles.socialProof}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0.5}
        >
          <div className={styles.avatarStack}>
            {/* Placeholder avatars using emoji backgrounds */}
            <div
              className={styles.avatar}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px"
              }}
            >
              &#128587;
            </div>
            <div
              className={styles.avatar}
              style={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px"
              }}
            >
              &#128131;
            </div>
            <div
              className={styles.avatar}
              style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px"
              }}
            >
              &#129333;
            </div>
            <div
              className={styles.avatar}
              style={{
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "14px"
              }}
            >
              &#128590;
            </div>
          </div>
          <span className={styles.socialText}>
            Join <span className={styles.socialHighlight}>{hero.socialProof.count}</span> {hero.socialProof.text}
          </span>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className={styles.ctaContainer}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0.6}
        >
          <DownloadButtonPair direction="row" />
        </motion.div>

        {/* Disclaimer */}
        <motion.p
          style={{
            marginTop: "1.5rem",
            fontSize: "0.75rem",
            color: "black",
            textAlign: "center",
            opacity: 0.9,
            fontWeight: 500,
            maxWidth: "600px",
          }}
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          custom={0.7}
        >
          {hero.disclaimer}
        </motion.p>
      </div>
    </HeroParallaxBase>
  );
}
